import { CalendarOutlined } from "@ant-design/icons";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { BookingCommonDetails } from "../../../booking/Booking.model";
import { UserAvailabilityDateTimeSlotsComponent } from "../../../calendar/UserAvailabilityDateTimeSlots.component";
import { apiLinkConstants, publicLinkConstants } from "../../../constants";
import { BogieName, EngineName } from "../../../Global.Names";
import { Loader } from "../../../loader/Loader";
import VideoSession, {
  ExpertSessionRole,
  VideoSessionRescheduleByMenteeStatus,
} from "../../../model/ExpertSession";
import { Api, ApiMethod } from "../ApiLinkedButton";
import { EBButtonV2Props } from "../EBButtonV2";
import { ApiLinkedMenuItem } from "../Form/ApiLinkedMenuItem";
import { EBTextAreaV2 } from "../Form/FormElements";
import { ModalLinkedMenuItem } from "../Form/ModalLinkedMenuItem";
import { FormikHiddenInput } from "../input/FormikHiddentInput";
import { reloadWindowPromise } from "../reloadCallback";

interface VideoSessionRescheduleComponentProps {
  session: VideoSession;
  bcd: BookingCommonDetails;
}

export function VideoSessionRescheduleStatusComponent(
  props: VideoSessionRescheduleComponentProps
) {
  return (
    <div className="VideoSessionRescheduleComponent mb-2">
      {(props.bcd.role === ExpertSessionRole.EXPERT ||
        props.bcd.role === ExpertSessionRole.ADMIN) &&
        props.session.expertRescheduleMarked && (
          <div className="text-danger">
            The current schedule is old one. It will be updated once {BogieName.SENTENCE_MID} picks a new
            slot. We have already notified and asked {BogieName.SENTENCE_MID} to
            update the same.
          </div>
        )}

      {(props.bcd.role === ExpertSessionRole.MENTEE ||
        props.bcd.role === ExpertSessionRole.ADMIN) &&
        props.session.expertRescheduleMarked && (
          <div className="text-danger mb-2">
            The current schedule is old one and the {EngineName.SENTENCE_MID} has requested to reschedule the session. Please pick a new slot by going to "Manage Session" button.
            <div className="text-dark mt-1">
              <b>Reason {EngineName.SENTENCE_MID} provided for reschedule:</b>{" "}
              {props.session.expertRescheduleNotes}
            </div>
          </div>
        )}
    </div>
  );
}

export function VideoSessionRescheduleMenuItems(
  props: VideoSessionRescheduleComponentProps
) {
  return (
    <>
      {(props.bcd.role === ExpertSessionRole.EXPERT ||
        props.bcd.role === ExpertSessionRole.ADMIN) && (
          <VideoSessionRescheduleExpertMenuItem {...props} />
        )}

      {(props.bcd.role === ExpertSessionRole.MENTEE ||
        props.bcd.role === ExpertSessionRole.ADMIN) && (
          <VideoSessionRescheduleMenteeMenuItem {...props} />
        )}
    </>
  );
}

export function VideoSessionRescheduleExpertMenuItem(
  props: VideoSessionRescheduleComponentProps
) {
  if (props.session.expertRescheduleMarked) return null;

  return (
    <ApiLinkedMenuItem
      uniqueKey={`${props.session.id}-expert`}
      label={`Request ${BogieName.SENTENCE_MID} to choose another slot`}
      icon={<CalendarOutlined />}
      api={
        new Api<{ comments: string }, void>(
          apiLinkConstants.API_VIDEO_SESSION_EXPERT_RESCHEDULE_REQUEST.replaceAll(
            "{sessionId}",
            props.session.id
          ),
          ApiMethod.POST
        )
      }
      successCallback={reloadWindowPromise}
      apiFormBody={
        <div>
          <EBTextAreaV2
            label={`Please provide a reason for asking to reschedule (The same will be shown to the ${BogieName.SENTENCE_MID}):`}
            name={"comments"}
            required={true}
            helpText=""
          />
          <div className="mt-2">
            A request will be sent to the {BogieName.SENTENCE_MID} for picking a
            new slot from your calendar.
          </div>

          <div className="alert alert-warning mt-1">
            Please make sure your availability is updated correctly in{" "}
            <a target="_blank" href={publicLinkConstants.EXPERT_STUDIO_CALENDAR}>
              Calendar Settings
            </a>{" "}
            before raising this reschedule request.
          </div>
        </div>
      }
    />
  );
}


export function VideoSessionRescheduleMenteeMenuItem(
  props: VideoSessionRescheduleComponentProps
) {
  let [rescheduleStatus, setRescheduleStatus] =
    useState<VideoSessionRescheduleByMenteeStatus | null>(null);
  let [loading, setLoading] = useState(false);
  let [isSubmitting, setSubmitting] = useState(false);

  const fetchStatus = () => {
    if (rescheduleStatus !== null || loading) return;
    setLoading(true);
    new Api<void, VideoSessionRescheduleByMenteeStatus>(
      apiLinkConstants.API_VIDEO_SESSION_MENTEE_RESCHEDULE_STATUS.replace(
        "{sessionId}",
        props.session.id
      ),
      ApiMethod.GET
    )
      .executeV2()
      .then(setRescheduleStatus)
      .finally(() => setLoading(false));
  };

  if (!props.session.expertRescheduleMarked) {
    let modalCloseBtnId = `${props.session.id}-mentee-modal-close-button`;

    const api = new Api<{ comments: string }, void>(
      apiLinkConstants.API_VIDEO_SESSION_MENTEE_RESCHEDULE.replaceAll(
        "{sessionId}",
        props.session.id
      ),
      ApiMethod.POST
    );

    const primaryBtnProps: EBButtonV2Props | undefined =
      rescheduleStatus?.isRescheduleAllowed
        ? {
          content: { normal: "Submit", submitting: "Submitting..." },
          type: "submit",
          otherProps: { form: "mentee-reschedule-form" },
          disabled: isSubmitting,
          loading: isSubmitting,
          purpose: "primary",
        }
        : undefined;

    return (
      <ModalLinkedMenuItem
        uniqueKey={`${props.session.id}-mentee`}
        label="Reschedule"
        icon={<CalendarOutlined />}
        onOpen={fetchStatus}
        modalHeader="Reschedule Session"
        primaryButtonProps={primaryBtnProps}
        formModalSize="xl"
        modalBody={
          loading ? (
            <div className="p-3 d-flex justify-content-center"><Loader />.</div>
          ) : !rescheduleStatus ? (
            <div className="p-3 text-center">Waiting to load reschedule status.</div>
          ) : !rescheduleStatus.isRescheduleAllowed ? (
            <div className="alert alert-danger mb-0">
              {rescheduleStatus.disallowDisplayMsg}
            </div>
          ) : (
            <Formik
              initialValues={{} as any}
              onSubmit={(values) => {
                setSubmitting(true);
                api
                  .executeV2(values)
                  .then(() => {
                    document.getElementById(modalCloseBtnId)?.click();
                    reloadWindowPromise();
                  })
                  .finally(() => {
                    setSubmitting(false);
                  });
              }}
            >
              <Form id="mentee-reschedule-form">
                <div className="row">
                  <div className="col-md-5">
                    <EBTextAreaV2
                      label={
                        "Reason for rescheduling (Same will be shown to the expert):"
                      }
                      name={"comments"}
                      required={true}
                    />
                  </div>
                  <div className="col-md-7">
                    <UserAvailabilityDateTimeSlotsComponent
                      userIdToShow={props.bcd.engine!.id}
                      durationMins={props.session.durationSeconds / 60}
                      onTimeSlotChange={() => { }}
                      name={"newSlot"}
                    />
                  </div>
                </div>
              </Form>
            </Formik>
          )
        }
      />
    );
  }

  return (
    <ApiLinkedMenuItem
      uniqueKey={`${props.session.id}-mentee-update`}
      label="Update Schedule"
      icon={<CalendarOutlined />}
      api={
        new Api<{ comments: string }, void>(
          apiLinkConstants.API_VIDEO_SESSION_MENTEE_SCHEDULE_UPDATE.replaceAll(
            "{sessionId}",
            props.session.id
          ),
          ApiMethod.POST
        )
      }
      successCallback={reloadWindowPromise}
      apiFormBody={
        <div className="row">
          <FormikHiddenInput
            name={"comments"}
            value={"Reschedule request from expert."}
          />

          <div className="col-md-12">
            <UserAvailabilityDateTimeSlotsComponent
              userIdToShow={props.bcd.engineId}
              durationMins={props.session.durationSeconds / 60}
              onTimeSlotChange={() => { }}
              name={"newSlot"}
            />
          </div>
        </div>
      }
    />
  );
}
